import { useCallback } from 'react';
import { debitNotesService } from '../services';
import {
  useResourceCollection,
  useResourceById,
  useCreateResource,
  useUpdateResource,
  useDeleteResource,
  ResourceState,
  SingleResourceState,
  MutationState,
} from './useResource';
import { ResourceQueryOptions } from '../services/resources.service';
import { DebitNote } from '../types';

export function useDebitNotes(
  options?: ResourceQueryOptions
): ResourceState<DebitNote> & { meta?: any } {
  const fetchFn = useCallback(
    (opts?: ResourceQueryOptions) =>
      debitNotesService.getAll(opts) as unknown as Promise<{ data: DebitNote[]; meta?: any }>,
    []
  );
  return useResourceCollection<DebitNote>(fetchFn, options);
}

export function useDebitNote(
  id: string | null,
  options?: ResourceQueryOptions
): SingleResourceState<DebitNote> {
  const resourceId = id;
  const fetchFn = useCallback(
    (debitNoteId: string | number, opts?: ResourceQueryOptions) =>
      debitNotesService.getById(debitNoteId, opts) as unknown as Promise<DebitNote>,
    []
  );
  return useResourceById<DebitNote>(fetchFn, resourceId, options);
}

export function useCreateDebitNote(
  onSuccess?: (data: DebitNote) => void
): MutationState<DebitNote> {
  const createFn = useCallback(
    (data: any) => debitNotesService.create(data) as unknown as Promise<DebitNote>,
    []
  );
  return useCreateResource<DebitNote>(createFn, onSuccess);
}

export function useUpdateDebitNote(
  onSuccess?: (data: DebitNote) => void
): MutationState<DebitNote> {
  const updateFn = useCallback(
    (id: string | number, data: any) => {
      return debitNotesService.update(id, data) as unknown as Promise<DebitNote>;
    },
    []
  );
  return useUpdateResource<DebitNote>(updateFn, onSuccess);
}

/**
 * Delete debit note using documentId
 * @param data - Full debit note object containing documentId
 */
export function useDeleteDebitNote(onSuccess?: () => void): MutationState<void> {
  const deleteFn = useCallback(
    (data: any) => {
      const documentId = data.documentId || data.id?.toString() || data.id;
      return debitNotesService.delete(documentId) as unknown as Promise<void>;
    },
    []
  );
  return useDeleteResource(deleteFn, onSuccess);
}

